import ko from 'knockout';
import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import styled from '@emotion/styled';

import SectionBase from '../classes/sectionbase';
import Router from '../utils/router';
import { useDocumentTitle } from '../../../main/scripts/utils/hooks';
import { recoverPassword } from '../../../main/scripts/utils/ApiUtilsV5';
import { Alert, Button, Field, Input } from '../../../main/scripts/lumen';
import { AuthCard, AuthHeading } from './login/AuthCard';

const TOO_MANY_REQUESTS_MESSAGE =
  'You have exceeded the number of password reset requests we allow in 24 hours. Either wait 24 hours to attempt another reset, or contact support@cemantica.com to reset your account.';

export default class ForgotPasswordSection extends SectionBase {
  sectionRequiresAuth = false;

  constructor() {
    super();
    ReactDOM.render(<ForgotPassword />, document.getElementById('sections'));
    ko.applyBindings(this);
  }
}

export const ForgotPassword = () => {
  useDocumentTitle('Forgot Password | Cemantica Insights hub');
  const [email, setEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [sending, setSending] = useState(false);

  const sendRecoveryEmail = event => {
    event.preventDefault();
    setErrorMessage('');
    setSending(true);

    recoverPassword(email)
      .then(() => {
        alert('Email sent.');
        Router.go('login.html');
      })
      .catch(data => {
        setSending(false);
        setErrorMessage(
          data.error === 'TOO_MANY_REQUESTS'
            ? TOO_MANY_REQUESTS_MESSAGE
            : data.message
        );
      });
  };

  return (
    <AuthCard>
      <AuthHeading>Forgot password</AuthHeading>
      <Intro>
        Enter your email address to receive a password-recovery email. Make sure
        to enter it correctly — if the address isn't a registered username, no
        email will be sent.
      </Intro>
      {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
      <Form onSubmit={sendRecoveryEmail}>
        <Field label="Email" htmlFor="forgot-email">
          <Input
            id="forgot-email"
            aria-label="email"
            type="text"
            autoFocus
            autoComplete="email"
            value={email}
            onChange={event => setEmail(event.target.value)}
          />
        </Field>
        <Button
          type="submit"
          variant="primary"
          size="md"
          disabled={sending}
          aria-busy={sending}
          style={{ width: '100%' }}
        >
          {sending ? 'Sending…' : 'Send email'}
        </Button>
        <BackLink href="login.html">Back to log in</BackLink>
      </Form>
    </AuthCard>
  );
};

const Intro = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.base};
  color: ${({ theme }) => theme.color.fg.muted};
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const BackLink = styled.a`
  font-size: ${({ theme }) => theme.fontSize.base};
  color: ${({ theme }) => theme.color.fg.default};
  line-height: 1.5;
  text-decoration: underline;
  text-underline-offset: 3px;
  text-align: center;

  &:hover {
    color: ${({ theme }) => theme.color.primary.hover};
  }
`;
